import React, { useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import * as api from "../services/api";
import { convertGoogleDriveUrlForDisplay, getPlaceholderImage } from "../utils/imageUtils";
import "./CategorySection.css";

// Fallback images for categories (if category doesn't have image)
import fruits from "../images/fruits.png";
import dairy from "../images/dairy.png";
import atta from "../images/atta.png";
import meat from "../images/meat.png";
import masala from "../images/masala.png";
import breakfast from "../images/breakfast.png";
import packaged from "../images/packaged.png";
import tea from "../images/tea.png";
import icecream from "../images/icecream.png";
import frozen from "../images/frozen.png";
import sweets from "../images/sweets.png";
import juices from "../images/juices.png";
import munchies from "../images/munchies.png";
import biscuits from "../images/biscuits.png";

// Map category names to images (fallback)
const categoryImageMap = {
  "fruits & vegetables": fruits,
  "dairy, bread & eggs": dairy,
  "atta, rice, oil & dals": atta,
  "meat, fish & eggs": meat,
  "masala & dry fruits": masala,
  "breakfast & sauces": breakfast,
  "packaged food": packaged,
  "tea, coffee & more": tea,
  "ice creams & more": icecream,
  "frozen food": frozen,
  "sweet cravings": sweets,
  "cold drinks & juices": juices,
  "munchies": munchies,
  "biscuits & cookies": biscuits,
};

const CategorySection = () => {
  const navigate = useNavigate();
  const [categories, setCategories] = useState([]);
  const [loading, setLoading] = useState(true);
  
  useEffect(() => {
    loadCategories();
  }, []);

  const loadCategories = async () => {
    try {
      setLoading(true);
      const categoriesData = await api.getCategories();
      console.log('=== CATEGORIES LOADED ===');
      categoriesData.forEach(cat => {
        console.log(`Category: ${cat.name}`, {
          _id: cat._id,
          hasImage: !!cat.image,
          imageType: cat.image ? typeof cat.image : 'none',
          imageValue: cat.image ? (cat.image.substring ? cat.image.substring(0, 100) : String(cat.image).substring(0, 100)) : 'none',
          fullCategory: cat
        });
      });
      setCategories(categoriesData);
    } catch (error) {
      console.error('Error loading categories:', error);
    } finally {
      setLoading(false);
    }
  };

  const getCategoryImage = (category) => {
    // First, use the image from the category if available
    // Check if image exists and is not empty string
    if (category.image && typeof category.image === 'string' && category.image.trim().length > 0) {
      const imageUrl = category.image.trim();
      
      // For base64 data URLs, use directly (don't convert)
      if (imageUrl.startsWith('data:image/')) {
        return imageUrl;
      }
      
      // For other URLs, convert Google Drive URLs if needed
      return convertGoogleDriveUrlForDisplay(imageUrl);
    }
    
    // Fallback to mapped images based on category name
    const nameLower = category.name.toLowerCase();
    return categoryImageMap[nameLower] || null;
  };

  const handleClick = (category) => {
    // Use the category slug if available, otherwise generate from name
    const slug = category.slug || category.name.toLowerCase()
      .replace(/[^a-z0-9]+/g, '-')
      .replace(/^-+|-+$/g, '');
    navigate(`/category/${slug}`);
  };

  if (loading) {
    return (
      <div className="category-container">
        <div style={{ padding: '2rem', textAlign: 'center' }}>
          <p>Loading categories...</p>
        </div>
      </div>
    );
  }

  if (categories.length === 0) {
    return (
      <div className="category-container">
        <div style={{ padding: '2rem', textAlign: 'center' }}>
          <p>No categories available.</p>
        </div>
      </div>
    );
  }

  return (
    <div className="category-container">
      <div className="category-section">
        <div className="category-grid">
          {categories.map((category) => {
            const categoryImage = getCategoryImage(category);
            const imageSrc = categoryImage || getPlaceholderImage(300, 300, category.name);
            
            // Debug: Log category image info
            console.log(`Rendering "${category.name}":`, {
              hasImageField: !!category.image,
              imageValue: category.image ? (typeof category.image === 'string' ? category.image.substring(0, 50) : String(category.image).substring(0, 50)) : 'none',
              getCategoryImageResult: categoryImage ? (categoryImage.substring ? categoryImage.substring(0, 50) : 'object') : 'null',
              finalImageSrc: imageSrc.substring ? imageSrc.substring(0, 50) : 'object'
            });
            
            return (
              <div
                key={category._id || category.slug}
                className="category-card"
                onClick={() => handleClick(category)}
              >
                <img
                  src={imageSrc}
                  alt={category.name}
                  className="category-image"
                  onError={(e) => {
                    // Prevent infinite retry loops
                    if (e.target.dataset.retryCount) {
                      const retryCount = parseInt(e.target.dataset.retryCount, 10);
                      if (retryCount >= 2) {
                        // Stop retrying after 2 attempts, use placeholder
                        e.target.src = getPlaceholderImage(300, 300, category.name);
                        e.target.onerror = null; // Remove error handler to prevent further retries
                        return;
                      }
                      e.target.dataset.retryCount = (retryCount + 1).toString();
                    } else {
                      e.target.dataset.retryCount = '1';
                    }

                    const originalSrc = e.target.src;
                    
                    // If base64 image fails, it might be corrupted - try fallback
                    if (originalSrc.startsWith('data:image/')) {
                      // Try fallback mapped image first
                      const fallback = categoryImageMap[category.name.toLowerCase()];
                      if (fallback && e.target.src !== fallback) {
                        e.target.src = fallback;
                        return;
                      }
                      // If no fallback, use placeholder
                      e.target.src = getPlaceholderImage(300, 300, category.name);
                      e.target.onerror = null;
                      return;
                    }

                    // Skip problematic Google URLs
                    if (originalSrc.includes('lh3.googleusercontent.com') || 
                        originalSrc.includes('drive.google.com/uc?export=download')) {
                      e.target.src = getPlaceholderImage(300, 300, category.name);
                      e.target.onerror = null;
                      return;
                    }

                    // Try alternative Google Drive formats if first attempt fails
                    if (originalSrc.includes('drive.google.com')) {
                      // Extract file ID
                      const fileIdMatch = originalSrc.match(/[?&]id=([a-zA-Z0-9_-]+)/) || 
                                        originalSrc.match(/\/file\/d\/([a-zA-Z0-9_-]+)/) ||
                                        originalSrc.match(/\/d\/([a-zA-Z0-9_-]+)/);
                      
                      if (fileIdMatch && fileIdMatch[1]) {
                        const fileId = fileIdMatch[1];
                        
                        // Try thumbnail method (more reliable)
                        if (!originalSrc.includes('thumbnail')) {
                          e.target.src = `https://drive.google.com/thumbnail?id=${fileId}&sz=w1000`;
                          return;
                        }
                      }
                    }
                    
                    // Try fallback mapped image
                    const fallback = categoryImageMap[category.name.toLowerCase()];
                    if (fallback && e.target.src !== fallback) {
                      e.target.src = fallback;
                      return;
                    }
                    
                    // Final fallback: use placeholder and stop retrying
                    e.target.src = getPlaceholderImage(300, 300, category.name);
                    e.target.onerror = null; // Remove error handler to prevent further retries
                  }}
                />
                <p className="category-label">{category.name}</p>
              </div>
            );
          })}
        </div>
      </div>
    </div>
  );
};

export default CategorySection;
